<script lang="ts" setup>
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import BalmDialog from '@/vue/atoms/balm-dialog.vue';
import {computed, ref, useId} from 'vue';
import type {CdcDppEligibleVariant} from '@/ts/composables/stateful/use-cdc-dpp-eligibility';

const props = defineProps<{
  eligible: boolean;
  variant: CdcDppEligibleVariant;
}>();

const emit = defineEmits<{
  continue: [];
  exit: [];
  checkInsurance: [];
}>();

const isOpen = defineModel<boolean>();
const showExitConfirmation = ref(false);

const eligibleHeadingId = useId();
const notEligibleHeadingId = useId();
const exitConfirmHeadingId = useId();

// The eligible body per variant: the participation-reward promise, or (NEUTRAL — no reward, no
// insurance to offer) just the eligibility statement. INSURANCE keeps its own template branch.
const eligibleBodyKey = computed(() =>
  (props.variant === 'REWARD' ? 'cdc_dpp_eligible_body_reward' : 'cdc_dpp_eligible_body_neutral'));

function onContinue(): void {
  isOpen.value = false;
  emit('continue');
}

function onCheckInsurance(): void {
  isOpen.value = false;
  emit('checkInsurance');
}

// REWARD/NEUTRAL eligible "Exit intervention" asks for confirmation first (the user is walking away
// from a recommended program — and, under REWARD, from the promised reward).
function onExitRequest(): void {
  showExitConfirmation.value = true;
}

function onConfirmQuit(): void {
  showExitConfirmation.value = false;
  isOpen.value = false;
  emit('exit');
}

function onStayEnrolled(): void {
  showExitConfirmation.value = false;
}

// The NOT-eligible modal exits straight from here. An INSURANCE-variant eligible user has no "Exit"
// button: the per-type terminals reached via "Continue" (or the insurance flow) own leaving, so the
// free-year offer is always seen before un-enrolling.
function onDirectExit(): void {
  isOpen.value = false;
  emit('exit');
}
</script>

<template>
  <!-- Modal A (INSURANCE): eligible, insurance funding can be offered -->
  <balm-dialog
    v-if="eligible && variant === 'INSURANCE'"
    v-model="isOpen"
    no-x
    persistent
    desktop-from-tablet
    no-scroll
    type="fit-content"
    theme="neutral-0"
    :aria-labelledby="eligibleHeadingId"
  >
    <div class="eligibility-modal">
      <span class="tone-icon tone-icon--success">
        <q-icon name="check" size="24px"/>
      </span>
      <h2 :id="eligibleHeadingId" class="modal-heading">
        {{ $t('cdc_dpp_eligible_heading') }}
      </h2>
      <p class="modal-body">
        {{ $t('cdc_dpp_eligible_body') }}
      </p>
      <div class="modal-actions">
        <balm-btn
          :label="$t('cdc_dpp_check_insurance')"
          @click="onCheckInsurance"
        />
        <balm-btn
          :label="$t('insurance_continue_without_btn')"
          theme="plain-underline"
          paddings="none"
          @click="onContinue"
        />
      </div>
    </div>
  </balm-dialog>

  <!-- Modal A (REWARD/NEUTRAL): eligible under the participation-reward regime (the promise copy),
       or with neither capability (eligibility info only). Continue goes straight into the session;
       Exit asks for confirmation. -->
  <balm-dialog
    v-else-if="eligible"
    v-model="isOpen"
    no-x
    persistent
    desktop-from-tablet
    no-scroll
    type="fit-content"
    theme="neutral-0"
    :aria-labelledby="eligibleHeadingId"
  >
    <div class="eligibility-modal">
      <span class="tone-icon tone-icon--success">
        <q-icon name="check" size="24px"/>
      </span>
      <h2 :id="eligibleHeadingId" class="modal-heading">
        {{ $t('cdc_dpp_eligible_heading') }}
      </h2>
      <p class="modal-body">
        {{ $t(eligibleBodyKey) }}
      </p>
      <div class="modal-actions">
        <balm-btn
          :label="$t('continue')"
          @click="onContinue"
        />
        <balm-btn
          :label="$t('cdc_dpp_exit_intervention')"
          theme="plain-underline"
          paddings="none"
          @click="onExitRequest"
        />
      </div>
    </div>
  </balm-dialog>

  <!-- Modal B: Not Eligible -->
  <balm-dialog
    v-else
    v-model="isOpen"
    no-x
    persistent
    desktop-from-tablet
    no-scroll
    type="fit-content"
    theme="neutral-0"
    :aria-labelledby="notEligibleHeadingId"
  >
    <div class="eligibility-modal">
      <h2 :id="notEligibleHeadingId" class="modal-heading">
        {{ $t('cdc_dpp_not_eligible_heading') }}
      </h2>
      <div class="modal-body">
        <p>{{ $t('cdc_dpp_not_eligible_body') }}</p>
        <p>{{ $t('cdc_dpp_not_eligible_note_intro') }}</p>
        <ul>
          <li>{{ $t('cdc_dpp_not_eligible_note_1') }}</li>
          <li>{{ $t('cdc_dpp_not_eligible_note_2') }}</li>
          <li>{{ $t('cdc_dpp_not_eligible_note_3') }}</li>
        </ul>
        <p>{{ $t('cdc_dpp_not_eligible_confirm') }}</p>
      </div>
      <div class="modal-actions">
        <balm-btn
          :label="$t('cdc_dpp_exit_intervention')"
          @click="onDirectExit"
        />
        <balm-btn
          :label="$t('cdc_dpp_continue_intervention')"
          theme="plain-underline"
          paddings="none"
          @click="onContinue"
        />
      </div>
    </div>
  </balm-dialog>

  <!-- Exit confirmation (REWARD/NEUTRAL eligible users only) -->
  <balm-dialog
    v-model="showExitConfirmation"
    no-x
    persistent
    desktop-from-tablet
    no-scroll
    type="fit-content"
    theme="neutral-0"
    :aria-labelledby="exitConfirmHeadingId"
  >
    <div class="eligibility-modal">
      <h2 :id="exitConfirmHeadingId" class="modal-heading">
        {{ $t('cdc_dpp_exit_confirm_heading') }}
      </h2>
      <p class="modal-body">
        {{ $t('cdc_dpp_exit_confirm_body') }}
      </p>
      <div class="modal-actions">
        <!-- Primary = the recommended (staying) action, matching every other CDC dialog in this
             flow; the destructive quit is the de-emphasized link. Deliberate inversion of the
             develop original, which predated that convention. -->
        <balm-btn
          :label="$t('cdc_dpp_stay_enrolled')"
          @click="onStayEnrolled"
        />
        <balm-btn
          :label="$t('cdc_dpp_quit')"
          theme="plain-underline"
          paddings="none"
          @click="onConfirmQuit"
        />
      </div>
    </div>
  </balm-dialog>
</template>

<style lang="scss" scoped>
.eligibility-modal {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--Spacing-spacing-6);
  text-align: center;
  gap: var(--Spacing-spacing-4);
  max-height: 90dvh;
  overflow-y: auto;

  @include screen-md {
    min-width: 500px;
    padding: var(--Spacing-spacing-8) var(--Spacing-spacing-8) var(--Spacing-spacing-6);
  }
}

.tone-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  // Constant light glyph on a saturated brand circle (same in light and dark mode).
  color: #fff;
}

.tone-icon--success {
  // Stable deep green in both themes.
  background-color: var(--Functional-wl-completed-surface);
}

.modal-heading {
  @include font-display-4;
  color: var(--Neutral-UI-neutral-ui-100);
  margin: 0;
}

.modal-body {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-70);
  margin: 0;
  max-width: 400px;
  line-height: 1.6;
  text-align: left;

  ul {
    padding-left: var(--Spacing-spacing-4);
    margin: var(--Spacing-spacing-2) 0;
  }

  li {
    margin-bottom: var(--Spacing-spacing-2);
  }
}

.modal-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--Spacing-spacing-3);
  width: 100%;

  // Primary is a full-width solid button; the secondary (plain-underline) stays a centered link.
  :deep(.balm-btn:not(.plain-underline)) {
    width: 100%;
  }
}
</style>
